import { useCallback, useMemo } from 'react';
import isNil from 'lodash/isNil';

import { EhrChatData } from 'chaika-frontend-types/frontend-cms';

import { store } from '@store';

import { MeatballButtonMenuItem } from '@components/ActionButtons/MeatballButton';
import { translate } from '@chaika/i18n/helper';
import { useEhrSubscription } from '@modules/patient/hooks/useEhrSubscription';
import { userList } from '@modules/user/store/UserList';
import { assertIsDefined } from '@utils/assertIsDefined';
import union from 'lodash/union';
import compact from 'lodash/compact';

interface Params {
    patientId: string;
    chats: EhrChatData[];
    doPatientHasChats: boolean;
    isUserBelongToPractice(practiceId: string): boolean;
    onPracticeChatCreation(): void;
    onChatCreation(): void;
}

interface Options {
    options: MeatballButtonMenuItem[];
}

export const useAssistantChatOptions = ({
    patientId,
    chats,
    doPatientHasChats,
    isUserBelongToPractice,
    onPracticeChatCreation,
    onChatCreation,
}: Params): Options => {
    const { authUser, ehr } = store;
    const { doPatientHasActiveSubscriptions, createTrialSubscription } = useEhrSubscription();

    const assistantChat = ehr.assistantsChats
        .filter(chat => !isNil(chat.joinLink) || !isNil(chat.registrationLink))
        .find(chat => isUserBelongToPractice(chat.practiceId));

    const openChat = useCallback(
        async (topicUrl: string, doctorId: string) => {
            const hasActiveSubscriptions = await doPatientHasActiveSubscriptions(patientId);

            if (!hasActiveSubscriptions) {
                await createTrialSubscription({ doctorId, patientId });
            }

            window.open(topicUrl, '_blank');
        },
        [patientId, doPatientHasActiveSubscriptions, createTrialSubscription],
    );

    const options = useMemo(() => {
        const assistantChatOption = isNil(assistantChat)
            ? {
                  label: 'createChatWithAssistants',
                  onClick: onPracticeChatCreation,
              }
            : {
                  label: 'chatWithAssistants',
                  onClick: () => {
                      window.open(assistantChat.joinLink ?? assistantChat.registrationLink, '_blank');
                  },
              };

        return [
            assistantChatOption,
            {
                label: doPatientHasChats ? translate('createChatWithDoctor') : translate('sendContactsButtonLabel'),
                onClick: onChatCreation,
            },
            ...chats
                .filter(
                    ({ practiceId, doctorId, patientId, registrationLink, joinLink }) =>
                        isNil(practiceId) &&
                        !isNil(patientId) &&
                        !isNil(doctorId) &&
                        compact(union(authUser.practiceDoctorIds, [authUser.id])).includes(doctorId) &&
                        [registrationLink, joinLink].some(link => !isNil(link)),
                )
                .map(chat => {
                    const { doctorId } = chat;
                    assertIsDefined(doctorId, 'Assistant chats are filtered by doctor above');

                    return {
                        label: `${translate('doctorsChat')} (${userList.get(doctorId)?.initials ?? doctorId})`,
                        onClick: () => {
                            const chatLink = chat.joinLink ?? chat.registrationLink;
                            assertIsDefined(chatLink, 'Assistant chats are filtered by having a link above');
                            openChat(chatLink, doctorId);
                        },
                    };
                }),
        ];
    }, [
        assistantChat,
        chats,
        doPatientHasChats,
        authUser.practiceDoctorIds,
        authUser.id,
        onPracticeChatCreation,
        onChatCreation,
        openChat,
    ]);

    return {
        options,
    };
};
